import React from 'react';
import { useTranslation } from 'react-i18next';
import type { RailAction } from '../../services/sse/railAction';
import styles from './TutorChat.module.css';

/**
 * Window event fired when the learner activates a rail action. Surfaces that can
 * act (editor line reveal, reader chapter open) subscribe to it; nothing here
 * moves the learner by itself. detail = the validated RailAction.
 */
export const RAIL_ACTION_EVENT = 'hauraki:rail-action';

interface Props {
  actions: RailAction[];
  onAct?: (action: RailAction) => void;
}

/** Learner-initiated shortcuts for the tutor's `rail_action` events. Renders nothing when empty. */
const RailActionChips: React.FC<Props> = ({ actions, onAct }) => {
  const { t } = useTranslation();
  if (actions.length === 0) return null;
  const activate = (action: RailAction) => {
    onAct?.(action);
    try {
      window.dispatchEvent(new CustomEvent(RAIL_ACTION_EVENT, { detail: action }));
    } catch {
      /* non-DOM environment */
    }
  };
  return (
    <div className={styles.railActions}>
      <span className={styles.srOnly} role="status" aria-live="polite">
        {t('tutor.railActions.announce', { count: actions.length })}
      </span>
      <div role="group" aria-label={t('tutor.railActions.label')} className={styles.railActionList}>
        {actions.map((a, i) => (
          <button
            key={`${a.kind}-${i}`}
            type="button"
            className={styles.railActionChip}
            onClick={() => activate(a)}
          >
            {a.kind === 'highlight_line'
              ? t('tutor.railActions.line', { line: a.line })
              : t('tutor.railActions.chapter')}
          </button>
        ))}
      </div>
    </div>
  );
};

export default RailActionChips;
